import React, { memo, useMemo } from 'react'
import * as THREE from 'three'
import { FURNITURE_CONFIG } from '../furnitureConfig'
import { usePanels } from '~/hooks/usePanel'
import { computeSidePanelDimensions } from '~/utils/computeSidePanelDimensions'

// Module-level constant: same reference across renders so downstream
// dep arrays don't invalidate on identity alone.
const SIDE_PANEL_ANCHOR = { anchorY: 'min' as const, anchorZ: 'min' as const }

interface SidePanelsProps {
  horizontalPanelObject: THREE.Object3D
  desiredWidth: number
  desiredHeight: number
  desiredDepth: number
  selectedColor: string
  isSingleNarrowColumn?: boolean
  isNewCollection?: boolean
  isTwoSingleDoorsException?: boolean
  isMirrored?: boolean
}

const SidePanelsComponent: React.FC<SidePanelsProps> = ({
  horizontalPanelObject: sidePanelSource,
  desiredWidth: cabinetWidth,
  desiredHeight: cabinetHeight,
  desiredDepth: cabinetDepth,
  selectedColor,
  isSingleNarrowColumn,
  isNewCollection,
  isTwoSingleDoorsException,
  isMirrored,
}) => {
  const { panelThickness } = FURNITURE_CONFIG
  const dims = computeSidePanelDimensions({
    width: cabinetWidth,
    height: cabinetHeight,
    depth: cabinetDepth,
    isNewCollection: !!isNewCollection,
    isSingleNarrowColumn: !!isSingleNarrowColumn,
    isTwoSingleDoorsException: !!isTwoSingleDoorsException,
    isMirrored: !!isMirrored,
  })

  const panelConfigs = useMemo(() => [
    {
      scale: [dims.thickness, dims.left.height, dims.left.width] as [number, number, number],
      position: [-cabinetWidth / 2 + panelThickness / 2, 0, 0] as [number, number, number],
      color: selectedColor,
      anchor: SIDE_PANEL_ANCHOR,
    },
    {
      scale: [dims.thickness, dims.right.height, dims.right.width] as [number, number, number],
      position: [cabinetWidth / 2 - panelThickness / 2, 0, 0] as [number, number, number],
      color: selectedColor,
      anchor: SIDE_PANEL_ANCHOR,
    },
  ], [dims.thickness, dims.left.height, dims.left.width, dims.right.height, dims.right.width, cabinetWidth, panelThickness, selectedColor])

  // Use the usePanels hook to create both panels at once
  const [leftSidePanel, rightSidePanel] = usePanels(sidePanelSource, panelConfigs)

  if (!leftSidePanel || !rightSidePanel) return null

  return (
    <group>
      <primitive key="left-side-panel" object={leftSidePanel} />
      <primitive key="right-side-panel" object={rightSidePanel} />
    </group>
  )
}

export const SidePanels = memo(SidePanelsComponent)